import { useEffect, useRef } from "react";
import type { Message } from "../../core/types";
import { useScroll } from "../../contexts/useScroll";
import { findReadingAnchor, lastRenderableMessageId } from "./readingAnchor";

/**
 * Scrolls the message list whenever a new message becomes renderable.
 *
 * The first renderable list seen while `enabled` (a thread opened from
 * history, or the first message of a new one) pins the bottom. After that an
 * assistant reply anchors the reader's question to the top of the list (see
 * `findReadingAnchor`), and everything else pins the bottom. A reply whose
 * bubble carries no anchor attribute falls back to the bottom pin.
 *
 * Keyed on the last renderable message's id rather than on the list length:
 * a reply can arrive with null contents first and become renderable on a
 * later update without the length changing.
 */
export const useReadingAnchor = (
  messages: Message[],
  enabled: boolean,
  /**
   * Identifies the thread the messages belong to. A page that stays mounted
   * across threads (CustomMainPage, or an embedder switching threads through
   * the core) starts over when it changes: the next thread's first list is
   * history, not a new reply.
   */
  threadKey: string | number | null,
): void => {
  const { anchorToMessage, scrollToBottom } = useScroll();
  const seenRef = useRef(false);
  const threadRef = useRef(threadKey);
  const lastId = lastRenderableMessageId(messages);

  useEffect(() => {
    if (threadRef.current !== threadKey) {
      threadRef.current = threadKey;
      seenRef.current = false;
    }
    if (!enabled || lastId === null) {
      seenRef.current = false;
      return;
    }
    const firstSight = !seenRef.current;
    seenRef.current = true;
    const anchorId = firstSight ? null : findReadingAnchor(messages);
    // Deferred so the scroll happens after React commits the new bubble.
    const timer = window.setTimeout(() => {
      if (anchorId === null || !anchorToMessage(anchorId)) {
        scrollToBottom();
      }
    }, 0);
    return (): void => window.clearTimeout(timer);
    // `messages` is read for the decision only; a new array with the same
    // last renderable id (a streaming update) must not scroll again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastId, enabled, threadKey, anchorToMessage, scrollToBottom]);
};
